import React, { useState, useRef, useCallback, useEffect } from 'react';
import { parseTaxInvoiceFile, TaxInvoiceRow, aggregateBySupplierMonth } from './parseTaxInvoice';

function getToken(): string {
  return sessionStorage.getItem('admin-token') || '';
}

function authHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getToken()}`,
  };
}

function TaxInvoiceManager() {
  const [allRows, setAllRows] = useState<TaxInvoiceRow[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [viewType, setViewType] = useState<'all' | '과세' | '면세'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [storedCount, setStoredCount] = useState(0);
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [selectedHalf, setSelectedHalf] = useState<'all' | '상반기' | '하반기'>('all');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  // 페이지 진입 시 Supabase에서 데이터 로드
  useEffect(() => {
    Promise.all([
      fetch('/api/tax', { headers: authHeaders() }).then(r => r.ok ? r.json() : []).then(d => Array.isArray(d) ? d : []),
      fetch('/api/tax/count', { headers: authHeaders() }).then(r => r.ok ? r.json() : { count: 0 }),
    ])
      .then(([rows, { count }]) => {
        setAllRows(rows);
        setStoredCount(count);
        setInitialLoading(false);
      })
      .catch(() => setInitialLoading(false));
  }, []);

  const processFiles = async (files: FileList) => {
    setLoading(true);
    let newRows: TaxInvoiceRow[] = [];
    let successCount = 0;

    const fileArray = Array.from(files).filter(f =>
      (f.name.endsWith('.xlsx') || f.name.endsWith('.xls')) && !f.name.startsWith('~$')
    );

    for (const file of fileArray) {
      try {
        const result = await parseTaxInvoiceFile(file);
        newRows = newRows.concat(result.rows);
        successCount++;
      } catch (e) {
        console.error(`Failed: ${file.name}`, e);
      }
    }

    if (newRows.length > 0) {
      // Supabase에 저장
      const saveResp = await fetch('/api/tax', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ rows: newRows }),
      });

      if (!saveResp.ok) {
        const errText = await saveResp.text();
        console.error('Save error:', errText);
        setLoading(false);
        showToast('저장 실패 - 콘솔 확인');
        return;
      }

      // 전체 다시 로드
      const [rows, countData] = await Promise.all([
        fetch('/api/tax', { headers: authHeaders() }).then(r => r.ok ? r.json() : []).then(d => Array.isArray(d) ? d : []),
        fetch('/api/tax/count', { headers: authHeaders() }).then(r => r.ok ? r.json() : { count: 0 }),
      ]);
      setAllRows(rows);
      setStoredCount(countData.count);
    }

    setLoading(false);
    if (successCount > 0) {
      showToast(`${successCount}개 파일에서 ${newRows.length}건 저장 완료`);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('저장된 모든 세금계산서 데이터를 삭제하시겠습니까?')) return;
    await fetch('/api/tax', { method: 'DELETE', headers: authHeaders() });
    setAllRows([]);
    setStoredCount(0);
    showToast('모든 데이터가 삭제되었습니다');
  };

  // 연도 목록 추출
  const availableYears = Array.from(new Set(allRows.map(r => r.date.substring(0, 4)))).sort().reverse();
  if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
    setSelectedYear(availableYears[0]);
  }

  // 필터: 과세/면세 → 연도 → 상반기/하반기
  const filteredRows = allRows.filter(r => {
    if (viewType !== 'all' && r.type !== viewType) return false;
    const year = r.date.substring(0, 4);
    if (year !== selectedYear) return false;
    if (selectedHalf !== 'all') {
      const month = parseInt(r.date.substring(5, 7), 10);
      if (selectedHalf === '상반기' && month > 6) return false;
      if (selectedHalf === '하반기' && month <= 6) return false;
    }
    return true;
  });
  const { suppliers: allSuppliers, allMonths } = aggregateBySupplierMonth(filteredRows);
  const suppliers = searchTerm
    ? allSuppliers.filter(s => s.supplierName.toLowerCase().includes(searchTerm.toLowerCase()))
    : allSuppliers;

  const grandTotal = suppliers.reduce(
    (acc, s) => ({
      count: acc.count + s.total.count,
      amount: acc.amount + s.total.amount,
      supplyAmount: acc.supplyAmount + s.total.supplyAmount,
      tax: acc.tax + s.total.tax,
    }),
    { count: 0, amount: 0, supplyAmount: 0, tax: 0 }
  );

  const fmt = (n: number) => n.toLocaleString();

  if (initialLoading) {
    return (
      <div>
        <h1 className="text-2xl font-light text-gray-800 mb-6">세금계산서 관리</h1>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-400">
          데이터 불러오는 중...
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-light text-gray-800 mb-6">세금계산서 관리</h1>

      {/* 업로드 영역 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-end gap-4 mb-4">
          <div className="text-sm text-gray-500">
            저장된 건수: <strong className="text-gray-800">{storedCount.toLocaleString()}건</strong>
            {allRows.length > 0 && (
              <span className="ml-3">
                (과세 {allRows.filter(r => r.type === '과세').length}건 / 면세 {allRows.filter(r => r.type === '면세').length}건)
              </span>
            )}
          </div>
          {storedCount > 0 && (
            <button
              onClick={handleClear}
              className="px-3 py-2 text-xs border border-gray-300 text-gray-500 rounded-lg hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors"
            >전체 삭제</button>
          )}
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); processFiles(e.dataTransfer.files); }}
          onClick={() => fileInputRef.current?.click()}
          className={`relative flex flex-col items-center justify-center py-10 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
            dragOver ? 'border-jeju-brown bg-jeju-brown/5' : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".xlsx,.xls"
            onChange={(e) => { if (e.target.files) { processFiles(e.target.files); e.target.value = ''; } }}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
          {loading ? (
            <div className="text-sm text-gray-500">파일 처리 중...</div>
          ) : (
            <>
              <div className="text-3xl mb-2 opacity-30">🧾</div>
              <p className="text-sm text-gray-400">세금계산서 엑셀 파일을 드래그하거나 클릭하여 선택하세요</p>
              <p className="text-xs text-gray-300 mt-1">과세(세금계산서) / 면세(계산서) 파일 모두 업로드 가능 · 기존 데이터에 누적 저장됩니다</p>
            </>
          )}
        </div>
      </div>

      {/* 집계 테이블 */}
      {allRows.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-20">
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="업체명 검색..."
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown focus:border-jeju-brown w-52"
              />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown focus:border-jeju-brown"
              >
                {availableYears.map(y => (
                  <option key={y} value={y}>{y}년</option>
                ))}
              </select>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                {(['all', '상반기', '하반기'] as const).map(h => (
                  <button
                    key={h}
                    onClick={() => setSelectedHalf(h)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      selectedHalf === h ? 'bg-jeju-brown text-white' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >{h === 'all' ? '전체' : h}</button>
                ))}
              </div>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                {(['all', '과세', '면세'] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => setViewType(t)}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      viewType === t ? 'bg-jeju-brown text-white' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >{t === 'all' ? '전체' : t}</button>
                ))}
              </div>
              <div className="ml-auto text-sm text-gray-500 whitespace-nowrap min-w-[200px] text-right">
                업체 {suppliers.length}개 · 합계금액 {fmt(grandTotal.amount)}원
              </div>
            </div>
          </div>

          <div className="overflow-auto max-h-[calc(100vh-420px)]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap border-b border-gray-200">구분</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap border-b border-gray-200">업체명</th>
                  {allMonths.map(m => (
                    <th key={m} className="text-right px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap border-b border-gray-200">{m}</th>
                  ))}
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap border-b border-gray-200">합계금액</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap border-b border-gray-200">공급가액</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap border-b border-gray-200">세액</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap border-b border-gray-200">건수</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {suppliers.map((s, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        s.type === '과세' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>{s.type}</span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-800 font-medium whitespace-nowrap">{s.supplierName}</td>
                    {allMonths.map(m => {
                      const d = s.months[m];
                      return (
                        <td key={m} className="px-4 py-2.5 text-right whitespace-nowrap" title={d ? `${d.count}건` : ''}>
                          {d ? (
                            <span className="text-gray-800">{fmt(d.amount)}</span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2.5 text-right text-gray-800 font-semibold whitespace-nowrap">{fmt(s.total.amount)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600 whitespace-nowrap">{fmt(s.total.supplyAmount)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600 whitespace-nowrap">{s.total.tax ? fmt(s.total.tax) : '-'}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600 whitespace-nowrap">{s.total.count}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-3" colSpan={2}>합계</td>
                  {allMonths.map(m => {
                    const monthTotal = suppliers.reduce((sum, s) => sum + (s.months[m]?.amount || 0), 0);
                    return (
                      <td key={m} className="px-4 py-3 text-right whitespace-nowrap text-gray-800">
                        {monthTotal ? fmt(monthTotal) : '-'}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-right text-gray-800 whitespace-nowrap">{fmt(grandTotal.amount)}</td>
                  <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">{fmt(grandTotal.supplyAmount)}</td>
                  <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">{grandTotal.tax ? fmt(grandTotal.tax) : '-'}</td>
                  <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">{grandTotal.count}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-jeju-brown text-white px-6 py-2.5 rounded-lg text-sm font-semibold z-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

export default TaxInvoiceManager;
